import React, { useState } from 'react';
import FeatherIcon from './FeatherIcon';
import { Link } from 'react-router-dom';
import { apiBase } from '../lib/api';

/**
 * Receipt self-service on the thank-you page, keyed on the Checkout session id (the bearer token
 * for these routes, valid 7 days): download the PDF, correct a mistyped email once, and reach the
 * donor portal to manage a recurring gift. A donor who typo'd their email gets no receipt and no
 * portal sign-in code, so this page is the only place they can recover from it.
 */

interface Props {
  readonly sessionId: string;
  /** Whether the one-time email correction is still available (from GET /session/:id). */
  readonly emailCorrectable: boolean;
  readonly recurring: boolean;
  /** Called with the address now on record after a successful correction. */
  readonly onEmailCorrected: (email: string) => void;
}

interface ApiResult {
  readonly status: number;
  readonly body: { url?: string; email?: string; code?: string; status?: string };
}

// ~30s in all: the webhook records the gift and issues the certificate a few seconds after Stripe
// redirects here, so the first click can arrive before either exists.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 8000, 8000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const call = async (sessionId: string, action: 'receipt' | 'donor-email', init?: RequestInit): Promise<ApiResult> => {
  const response = await fetch(`${apiBase}/api/donations/session/${encodeURIComponent(sessionId)}/${action}`, init);
  return { status: response.status, body: await response.json().catch(() => ({})) };
};

/** Repeats `request` while the gift is still being recorded (409 NOT_YET_RECORDED) or the receipt is pending (202). */
const untilRecorded = async (request: () => Promise<ApiResult>): Promise<ApiResult> => {
  let result = await request();
  for (const delay of RETRY_DELAYS_MS) {
    const pending = result.status === 202 || (result.status === 409 && result.body.code === 'NOT_YET_RECORDED');
    if (!pending) return result;
    await sleep(delay);
    result = await request();
  }
  return result;
};

const linkButtonClass =
  'inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border border-gray-300 bg-card font-medium text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-60';

const ReceiptActions: React.FC<Props> = ({ sessionId, emailCorrectable, recurring, onEmailCorrected }) => {
  const [download, setDownload] = useState<{ busy: boolean; message: string | null }>({ busy: false, message: null });
  const [correctable, setCorrectable] = useState(emailCorrectable);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [fixMessage, setFixMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const downloadReceipt = async () => {
    // Opened synchronously in the click so popup blockers allow it; the URL arrives after the fetch
    // (and any retries). Not `noopener` — that makes window.open return null, leaving no handle to navigate.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setDownload({ busy: true, message: null });
    try {
      const result = await untilRecorded(() => call(sessionId, 'receipt'));
      if (result.status === 200 && result.body.url) {
        // A 5-minute presigned link, minted for this click.
        if (tab) tab.location.href = result.body.url; else window.location.assign(result.body.url);
        setDownload({ busy: false, message: null });
        return;
      }
      tab?.close();
      if (result.status === 202 || result.status === 409) {
        setDownload({ busy: false, message: 'Your receipt is still being prepared. It will arrive by email and in the donor portal — or try again in a minute.' });
      } else if (result.status === 404) {
        setDownload({ busy: false, message: 'There is no receipt for this gift.' });
      } else {
        setDownload({ busy: false, message: 'We couldn’t get your receipt just now. Please try again.' });
      }
    } catch {
      tab?.close();
      setDownload({ busy: false, message: 'We couldn’t reach our servers. Please try again.' });
    }
  };

  const correctEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = newEmail.trim();
    if (!email) return;
    setSaving(true);
    setFixMessage(null);
    try {
      const result = await untilRecorded(() => call(sessionId, 'donor-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      }));
      if (result.status === 200 && result.body.email) {
        onEmailCorrected(result.body.email);
        setCorrectable(false);
        setEditing(false);
        setFixMessage({ tone: 'ok', text: `Done — your receipt was sent to ${result.body.email}. Use this address to sign in to the donor portal.` });
      } else if (result.status === 400) {
        setFixMessage({ tone: 'error', text: 'That doesn’t look like a valid email address.' });
      } else if (result.status === 409 && result.body.code === 'ALREADY_CORRECTED') {
        setCorrectable(false);
        setEditing(false);
        setFixMessage({ tone: 'error', text: 'This gift’s email has already been corrected once. For further changes, contact info@pawsitivefuture.org.' });
      } else if (result.status === 404) {
        setCorrectable(false);
        setEditing(false);
        setFixMessage({ tone: 'error', text: 'This gift’s email can no longer be changed here. Contact info@pawsitivefuture.org for help.' });
      } else {
        setFixMessage({ tone: 'error', text: 'We couldn’t update your email just now. Please try again.' });
      }
    } catch {
      setFixMessage({ tone: 'error', text: 'We couldn’t reach our servers. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 space-y-3 text-left">
      <button type="button" onClick={downloadReceipt} disabled={download.busy} className={linkButtonClass}>
        <FeatherIcon name="download" size={18} />
        {download.busy ? 'Preparing your receipt…' : 'Download PDF receipt'}
      </button>
      {download.message && <p className="text-sm text-gray-600 text-center" role="status">{download.message}</p>}

      {correctable && !editing && (
        <p className="text-sm text-center">
          <button type="button" onClick={() => setEditing(true)} className="text-blue-700 underline font-medium">
            Wrong email? Fix it
          </button>
        </p>
      )}

      {editing && (
        <form onSubmit={correctEmail} className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
          <label htmlFor="corrected-email" className="block text-sm font-medium text-gray-800">
            Correct email address
          </label>
          <input
            id="corrected-email"
            type="email"
            required
            autoComplete="email"
            autoFocus
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
          />
          <p className="text-xs text-gray-600">
            This can be changed only once — check it carefully. Your receipt is re-sent to it, and future
            receipts and sign-in codes go there.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="submit" disabled={saving} className="btn-primary px-3 py-2 rounded-lg font-medium disabled:opacity-60">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => { setEditing(false); setFixMessage(null); }}
              className="px-3 py-2 rounded-lg border border-gray-300 bg-card font-medium text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {fixMessage && (
        <p className={`text-sm text-center break-words ${fixMessage.tone === 'ok' ? 'text-green-700' : 'text-red-700'}`} role="status">
          {fixMessage.text}
        </p>
      )}

      {recurring && (
        <Link to="/sign-in" className={linkButtonClass}>
          <FeatherIcon name="repeat" size={18} />
          Manage or cancel this recurring gift
        </Link>
      )}
    </div>
  );
};

export default ReceiptActions;
